/**
 * Phân quyền theo TÀI KHOẢN: tick từng menu, chọn hành động, gán danh sách khách.
 *
 * Vì sao có tệp này, khi đã có `rbac.ts` suy quyền từ vai trò:
 * khách yêu cầu "không chỉ 4 role cứng" — mỗi nhân viên được vào những menu
 * khác nhau và chỉ thấy dữ liệu của những khách được giao. Bốn vai trò cũ
 * KHÔNG bị bỏ; chúng trở thành **bộ mẫu** (preset) và vẫn là chế độ mặc định
 * của mọi tài khoản đang chạy, nên không tài khoản nào mất quyền vào ngày
 * triển khai. Admin bật chế độ "tuỳ chỉnh" cho từng người khi cần.
 *
 * Đặt ở `shared` vì cả sidebar lẫn máy chủ phải đọc CÙNG một bảng — nếu web tự
 * hiểu một kiểu và API hiểu kiểu khác thì người dùng thấy menu rồi ăn 403,
 * hoặc tệ hơn: menu bị ẩn nhưng đường dẫn gõ tay vẫn vào được.
 *
 * ĐÂY LÀ NGUỒN SỰ THẬT DUY NHẤT. Máy chủ chặn ở `requireAuth`; giao diện chỉ
 * dùng lại để khỏi vẽ nút mà bấm vào là lỗi (golden rule 4: ẩn nút không phải
 * hàng rào).
 */

import { ROLE_PERMISSIONS, canReadResource, hasPermission, type Permission, type Role } from "./rbac";
import type { CustomerClass } from "./dto";

/* ─────────────────────────────  Hành động  ───────────────────────────── */

/**
 * Năm hành động được chặn THẬT ở máy chủ.
 *
 * Bốn hành động đầu là thang chuẩn của mọi ERP (NetSuite: View / Create / Edit
 * / Full; Odoo: read / create / write / unlink). "Duyệt" là hành động thứ năm,
 * tách riêng vì đó là cách SAP (mã duyệt M_EINK_FRG), Business Central (bảng
 * người duyệt) và bảng phân quyền của khách (16/09/2026) đều làm: người lập
 * chứng từ và người duyệt nó phải là hai ô tick khác nhau, nếu không thì
 * nguyên tắc "tạo và duyệt không cùng một người" không thể cấu hình được.
 * Duyệt chỉ có nghĩa ở những chứng từ có bước duyệt thật (xem `DECISION_RULES`
 * và `APPROVE_SEGMENTS`); ở màn hình khác ô tick này không làm gì.
 *
 * Không có "in/xuất" ở đây là có chủ ý: mọi bản in và tệp CSV của hệ này được
 * dựng trong trình duyệt TỪ dữ liệu đã tải về để hiển thị (xem
 * `apps/web/src/lib/exportCsv.ts`). Ai xem được màn hình thì đã có sẵn dữ liệu
 * trong tay, nên một ô tick "cấm xuất" chỉ giấu cái nút chứ không chặn được gì
 * — ghi nó vào bảng quyền là nói dối người quản trị. Muốn cấm xuất thật thì
 * phải cấm xem.
 */
export const ACCESS_VERBS = ["view", "create", "update", "delete", "approve"] as const;
export type AccessVerb = (typeof ACCESS_VERBS)[number];

/* ─────────────────────────────  Danh mục menu  ────────────────────────── */

export interface MenuDef {
  /** Trùng khoá mục trên sidebar (`apps/web/src/app/nav.ts`) để dùng chung nhãn i18n. */
  key: string;
  /** Nhóm trên sidebar — chỉ để xếp bảng tick cho dễ nhìn. */
  group: string;
  /** Tiền tố đường dẫn API thuộc menu này, KHÔNG có `/api`. */
  prefixes: readonly string[];
  /** Menu có gắn với khách hàng → chịu giới hạn "chỉ khách được giao". */
  customerScoped?: boolean;
  /** Quyền cũ tương đương, để chế độ "theo vai trò" và chế độ tuỳ chỉnh khớp nhau. */
  permission?: Permission;
  /** Tên tài nguyên mà `requireResourceRead` đang dùng, nếu có. */
  resource?: string;
  /**
   * Trang đã ẩn khỏi giao diện (cùng cờ với `nav.ts`). Vẫn giữ trong bảng để API
   * của nó có menu phụ trách (mặc định đóng), nhưng bảng phân quyền không vẽ
   * dòng cho nó — admin không nên cấp quyền vào một chỗ không ai thấy.
   */
  hidden?: boolean;
}

/**
 * Mọi tiền tố API đều phải nằm trong bảng này hoặc trong `ALWAYS_ALLOWED`.
 *
 * Đường dẫn không khớp gì cả sẽ bị TỪ CHỐI với tài khoản tuỳ chỉnh. Mặc định
 * đóng, không mặc định mở: thêm route mới mà quên khai báo thì lỗi lộ ra ngay
 * ở môi trường thử, chứ không phải là một cửa hậu im lặng.
 */
export const MENUS: readonly MenuDef[] = [
  { key: "dashboard", group: "overview", prefixes: ["/dashboard"], permission: "dashboard:view" },
  { key: "analytics", group: "overview", prefixes: ["/analytics"], permission: "analytics:view" },
  { key: "profit", group: "overview", prefixes: ["/analytics/profit"], permission: "supplier_cost:view" },
  { key: "piStatus", group: "overview", prefixes: ["/analytics/pi-status"], customerScoped: true },
  // Quy trình thường (29/10/2026): đơn KHÔNG chạy quy trình P đang ở bước nào — cùng hàng rào khách / PI chỉ quản trị viên như màn P.
  { key: "orderProcess", group: "overview", prefixes: ["/order-process"], resource: "order-process", customerScoped: true },
  // 16/09/2026: trang không có tiền → nhân viên nội bộ đều xem được (kế toán vẫn ngoài, theo `ACCOUNTANT_READABLE`).
  { key: "kpis", group: "overview", prefixes: ["/kpis"], resource: "kpis" },

  { key: "products", group: "masterData", prefixes: ["/products", "/product-files"], resource: "products" },
  { key: "customers", group: "masterData", prefixes: ["/customers"], resource: "customers", customerScoped: true },
  { key: "suppliers", group: "masterData", prefixes: ["/suppliers"], resource: "suppliers" },
  { key: "unitPricing", group: "masterData", prefixes: ["/unit-pricing"], permission: "supplier_cost:view" },

  { key: "sampleOrders", group: "samples", prefixes: ["/sample-orders"], resource: "sample-orders", customerScoped: true },
  { key: "sampleCards", group: "samples", prefixes: ["/sample-cards"], resource: "sample-cards", customerScoped: true },

  { key: "quotations", group: "sales", prefixes: ["/quotations"], resource: "quotations", customerScoped: true },
  { key: "opportunities", group: "sales", prefixes: ["/sales-opportunities"], permission: "supplier_cost:view", customerScoped: true, hidden: true },
  { key: "salesOrders", group: "sales", prefixes: ["/sales-orders"], permission: "supplier_cost:view", customerScoped: true },
  { key: "proformaInvoices", group: "sales", prefixes: ["/proforma-invoices"], resource: "proforma-invoices", customerScoped: true },
  { key: "invoices", group: "sales", prefixes: ["/invoices"], resource: "invoices", customerScoped: true },
  { key: "serviceTickets", group: "sales", prefixes: ["/service-tickets"], resource: "service-tickets", customerScoped: true },

  { key: "mrp", group: "procurement", prefixes: ["/mrp"], permission: "supplier_cost:view" },
  { key: "requisitions", group: "procurement", prefixes: ["/purchase-requisitions"], permission: "supplier_cost:view" },
  { key: "sourcing", group: "procurement", prefixes: ["/sourcing"], permission: "supplier_cost:view" },
  { key: "purchaseOrders", group: "procurement", prefixes: ["/purchase-orders"], permission: "supplier_cost:view" },
  // 17/09/2026: khiếu nại / phạt nhà cung cấp (Louis). Có tiền đòi nhà cung cấp → cùng cửa giá vốn như đơn mua.
  { key: "supplierClaims", group: "procurement", prefixes: ["/supplier-claims"], permission: "supplier_cost:view" },
  // 18/09/2026: cổng nhà cung cấp thuộc Mua hàng, Quét & tem thuộc Kho — cùng chỗ với sidebar (nav.ts).
  { key: "portal", group: "procurement", prefixes: ["/portal"], permission: "supplier_cost:view" },

  { key: "warehouse", group: "inventoryDelivery", prefixes: ["/warehouses", "/warehouse-receipts"], resource: "warehouses" },
  // 30/10/2026 (YC-07 phần 1): danh mục vị trí kho và tồn theo vị trí — menu riêng để tick riêng; quyền mặc định như Kho & LOT.
  { key: "warehouseLocations", group: "inventoryDelivery", prefixes: ["/warehouse-bins"], resource: "warehouses" },
  { key: "stockMoves", group: "inventoryDelivery", prefixes: ["/stock-moves", "/lot-transfers"], permission: "supplier_cost:view" },
  { key: "packingLists", group: "inventoryDelivery", prefixes: ["/packing-lists"], resource: "packing-lists", customerScoped: true },
  { key: "deliveryNotes", group: "inventoryDelivery", prefixes: ["/delivery-notes"], resource: "delivery-notes", customerScoped: true },
  { key: "devices", group: "inventoryDelivery", prefixes: ["/devices"], permission: "supplier_cost:view" },

  { key: "boms", group: "manufacturingQuality", prefixes: ["/boms"], permission: "supplier_cost:view" },
  { key: "routings", group: "manufacturingQuality", prefixes: ["/routings"], permission: "supplier_cost:view" },
  { key: "production", group: "manufacturingQuality", prefixes: ["/production", "/work-orders"], permission: "supplier_cost:view" },
  /*
   * `/iqc` nằm ở đây, không phải ở Kho & LOT (chuyển 20/09/2026).
   *
   * Màn hình kiểm hàng đầu vào mở từ mục "Chất lượng" trên thanh bên và đường
   * dẫn web là `/quality`, nhưng tiền tố API `/iqc` lại được khai ở menu
   * `warehouse`. Hậu quả: tài khoản được tick ĐÚNG mục Chất lượng mở màn hình
   * ra rồi nhận 403 cho mọi lời gọi, còn tài khoản chỉ được tick Kho & LOT lại
   * duyệt được phiếu kiểm — đúng thứ mà "quyền chỉ đến từ MỘT nguồn" cấm.
   * `/inspections` VẪN Ở ĐÂY: nó là route ERP dựng động từ `ERP_ENTITIES` (bảng
   * `inspections` cũ, số hiệu QC-…), nên `grep "route(\"/inspections"` không ra —
   * ngày 20/09/2026 tôi đã bỏ nhầm nó một lần vì tưởng là tiền tố chết, làm mọi
   * lời gọi `/api/inspections` của tài khoản phân quyền riêng rơi vào "mặc định
   * ĐÓNG". Bài kiểm `access-routes.test.ts` nay khoá lại chuyện đó.
   */
  { key: "quality", group: "manufacturingQuality", prefixes: ["/iqc", "/quality-specs", "/ncr", "/inspections"], permission: "supplier_cost:view" },
  { key: "pProcess", group: "manufacturingQuality", prefixes: ["/p-process"], resource: "p-process", customerScoped: true },

  { key: "statement", group: "financeReceivables", prefixes: ["/statements"], resource: "statements", customerScoped: true },
  { key: "finance", group: "financeReceivables", prefixes: ["/finance", "/journal-entries"], resource: "journal-entries" },
  { key: "glAccounts", group: "financeReceivables", prefixes: ["/gl-accounts"], resource: "gl-accounts" },

  { key: "users", group: "admin", prefixes: ["/users"], permission: "users:manage" },
  { key: "settings", group: "admin", prefixes: ["/settings", "/companies"], permission: "settings:manage" },
  { key: "deletedRecords", group: "admin", prefixes: ["/deleted-records"], permission: "deleted:manage" },
];

export const MENU_KEYS: readonly string[] = MENUS.map((m) => m.key);

/** Menu nào bị giới hạn theo danh sách khách được giao. */
export const CUSTOMER_SCOPED_MENUS: ReadonlySet<string> = new Set(
  MENUS.filter((m) => m.customerScoped).map((m) => m.key),
);

/**
 * Những đường dẫn ai đăng nhập cũng đi được.
 *
 * `/auth` — không cho thì không ai đăng xuất hay đổi mật khẩu được.
 * `/health`, `/releases` — hạ tầng, không có dữ liệu kinh doanh.
 * `/feature-preview` — mở khoá tính năng đang làm; quyền vào chính tính năng vẫn theo menu của nó.
 * `/files` — phát ảnh/tệp đã tải lên theo khoá R2; ảnh sản phẩm nằm trong mọi
 *   màn hình, chặn ở đây là vỡ giao diện của chính những trang người dùng
 *   ĐƯỢC xem. Bản thân khoá R2 không đoán được.
 * `/mail` — chỉ trả về cấu hình hộp thư và gửi bản nháp do client dựng; nội
 *   dung chứng từ đã bị chặn ở chính menu của nó.
 */
export const ALWAYS_ALLOWED_PREFIXES: readonly string[] = [
  "/auth",
  "/health",
  "/releases",
  "/feature-preview",
  "/files",
  "/mail",
];

/**
 * Đúng MỘT đường dẫn này, không tính đường dẫn con.
 *
 * `GET /companies` chỉ trả id + tên pháp nhân, dùng cho cột "công ty nhận tiền"
 * trên chính PI và hoá đơn. Chặn nó thì cột đó trống trên chứng từ mà người
 * dùng ĐƯỢC xem. Số tài khoản ngân hàng nằm ở `/companies/detail` và các đường
 * ghi — tất cả vẫn thuộc menu Cài đặt, nên KHÔNG được miễn theo tiền tố.
 */
export const ALWAYS_ALLOWED_EXACT: readonly string[] = ["/companies"];

/**
 * Danh mục TRA CỨU (01/10/2026): đọc DANH SÁCH để điền ô chọn trên chứng từ, không cần ô Xem ở menu của chính danh mục.
 *
 * Tìm ra khi Stephen (tài khoản tuỳ chỉnh, tick 29/09/2026: đủ Đơn mẫu, Đơn mua, Sản phẩm nhưng không Cài đặt, không Nhà cung
 * cấp) lập đơn mẫu: ô Loại trống trơn vì `GET /settings` bị 403, ô nhà cung cấp báo lỗi vì `GET /suppliers` bị 403 — không lập
 * được đơn. Bộ mẫu Trưởng phòng kinh doanh và Nhân viên theo đơn cũng có Đơn mẫu mà không có hai menu đó, nên đây là lỗi của hệ,
 * không phải người quản trị tick sai.
 *
 * Chuẩn ERP: quyền lập chứng từ kéo theo quyền ĐỌC bản ghi mà chứng từ trỏ tới (Odoo: Purchase User đọc được đối tác; Business
 * Central: bộ quyền mua hàng có Read trên bảng Vendor); mở màn hình danh mục và sửa nó là quyền riêng. Vì vậy:
 * - chỉ GET/HEAD, chỉ ĐÚNG đường danh sách — `/suppliers/:id`, mọi lệnh ghi, bộ đếm và lịch sử cài đặt vẫn thuộc menu của chúng;
 * - `"*"` = mọi tài khoản đăng nhập. `GET /settings` là danh mục dropdown, thông tin công ty in trên chứng từ, mặc định hệ thống,
 *   cỡ tem — form nào cũng cần; tài khoản theo vai trò vốn luôn đọc được. Số tài khoản ngân hàng ở `/companies/detail`, không ở đây;
 * - danh sách menu = có ô Xem ở MỘT menu có ô chọn đó. Khu vực dữ liệu vẫn lọc nhà cung cấp y như ở màn Nhà cung cấp.
 *
 * `/users/options` (11/10/2026): ô chọn người — "Người phụ trách" trên PI (yêu cầu v1.8 mục 4.2) và "Người theo đơn" trên hồ sơ
 * khách. Đường này thuộc menu Người dùng nên tài khoản tuỳ chỉnh không tick menu đó bị 403 và ô chọn trống trơn (tài khoản theo vai
 * trò thì qua). Chỉ trả id + tên hiển thị của người đang hoạt động — không email, vai trò, trạng thái; `/users`, `/users/:id` và
 * mọi lệnh ghi vẫn thuộc menu Người dùng.
 */
export const LOOKUP_READS: Readonly<Record<string, "*" | readonly string[]>> = {
  "/settings": "*",
  "/suppliers": ["products", "sampleOrders", "purchaseOrders", "portal"],
  "/users/options": ["proformaInvoices", "customers"],
};

export function canLookup(access: UserAccess, method: string, path: string): boolean {
  const m = method.toUpperCase();
  if (m !== "GET" && m !== "HEAD") return false;
  const clean = (path.replace(/^\/api/, "").split("?")[0] ?? "").replace(/\/$/, "") || "/";
  const menus = LOOKUP_READS[clean];
  if (!menus) return false;
  return menus === "*" || menus.some((k) => canAccess(access, k, "view"));
}

/* ────────────────────  Đường dẫn/phương thức → menu, hành động  ───────── */

// Dài trước ngắn: "/analytics/profit" phải thắng "/analytics".
const MENU_BY_PREFIX: ReadonlyArray<readonly [string, MenuDef]> = MENUS.flatMap((m) =>
  m.prefixes.map((p) => [p, m] as const),
).sort((a, b) => b[0].length - a[0].length);

function matches(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
}

/** Menu phụ trách đường dẫn API này (đã bỏ `/api`), hoặc `undefined`. */
export function menuForPath(path: string): MenuDef | undefined {
  const clean = path.replace(/^\/api/, "").split("?")[0] ?? "";
  return MENU_BY_PREFIX.find(([prefix]) => matches(clean, prefix))?.[1];
}

export function isAlwaysAllowed(path: string): boolean {
  const clean = (path.replace(/^\/api/, "").split("?")[0] ?? "").replace(/\/$/, "") || "/";
  return ALWAYS_ALLOWED_EXACT.includes(clean) || ALWAYS_ALLOWED_PREFIXES.some((p) => matches(clean, p));
}

/**
 * Những đoạn cuối đường dẫn là HÀNH ĐỘNG trên bản ghi có sẵn, không phải tạo mới.
 *
 * Chúng đi bằng POST theo lệ REST, nhưng người chỉ được "sửa" mà không được
 * "tạo" vẫn phải duyệt/huỷ/chốt được — nếu không thì ô tick "sửa" gần như vô
 * dụng ở các màn hình chứng từ.
 */
const ACTION_SEGMENTS = new Set([
  // `/:id/action(s)` với thân {action} là thao tác trên bản ghi có sẵn. Trước
  // 16/09/2026 chúng bị coi là "tạo", nên người chỉ được tạo vẫn huỷ/đóng được.
  "action", "actions", "status",
  "cancel", "close", "reopen", "post", "reverse", "void",
  "issue", "receive", "complete", "start", "finish", "submit",
  "recalculate", "recompute", "sync", "restore", "archive", "unarchive",
  "abort", "complete-upload", "parts", "send",
  // 20/09/2026 (Biến động kho): gửi hàng đi kho khác và giải phóng giữ hàng là việc của ô Sửa — trước đó rơi vào "tạo".
  "ship", "release",
]);

/**
 * Đoạn cuối đường dẫn là một QUYẾT ĐỊNH DUYỆT, không phải sửa.
 *
 * `/iqc/:id/approve`, `/warehouse-receipts/:id/confirm`,
 * `/lot-transfers/:id/confirm` — bấm vào là chứng từ đổi sang trạng thái có
 * hiệu lực và khoá lại. Người chỉ được "sửa" thì vẫn sửa nháp được nhưng không
 * chốt được; đó chính là chỗ tách người lập và người duyệt.
 */
// `award` (19/09/2026): chọn chào giá và tạo đơn mua nháp ở Yêu cầu báo giá là quyết định của người có ô Duyệt (SOURCING-R01).
const APPROVE_SEGMENTS = new Set([
  "approve", "reject", "confirm", "award",
  /*
   * 20/09/2026 (Định mức vật tư): duyệt và ngừng áp dụng một phiên bản định
   * nghĩa (BOM, quy trình công đoạn, bảng giá đơn vị) là QUYẾT ĐỊNH, không phải
   * "tạo". Trước đó hai đoạn này không nằm trong tập nào nên `verbForRequest`
   * trả về "create": tài khoản chỉ được tick Tạo vẫn duyệt được một định mức
   * và ngừng bản đang áp dụng của cả nhà máy.
   */
  "activate", "retire",
]);

/**
 * Đoạn cuối đường dẫn là một phép TÍNH THỬ, không ghi gì (19/09/2026).
 *
 * `POST /mrp/preview`, `POST /production/preview` chỉ tính và trả kết quả — không số chứng từ, không giữ hàng, không ghi kho hay
 * tiền. Đi bằng POST vì đầu vào dài, nhưng theo nghĩa là "xem": người được tick Xem ở MRP phải chạy thử được; ghi lại thành kịch bản
 * (`/mrp/runs`) mới cần ô Tạo.
 */
const READ_SEGMENTS = new Set(["preview"]);

/**
 * Những đường dẫn mà hành động thật nằm TRONG THÂN yêu cầu.
 *
 * `PUT /purchase-orders/:id/status {status}` và `POST /sales-orders/:id/actions
 * {action}` dùng một đường cho cả "gửi nháp" lẫn "xác nhận". Theo đường dẫn thì
 * chỉ biết là "sửa"; phải đọc thân mới biết có phải duyệt không. `requireAuth`
 * cho người có QUYỀN DUYỆT hoặc QUYỀN SỬA đi qua đường này, rồi route đọc thân
 * và gọi `decisionVerb` để chặn đúng hành động (xem `requireDecision`).
 */
const DECISION_SEGMENTS = new Set(["status", "action", "actions"]);

export function isDecisionPath(path: string): boolean {
  const last = (path.split("?")[0] ?? "").split("/").filter(Boolean).pop() ?? "";
  return DECISION_SEGMENTS.has(last);
}

/**
 * Giá trị nào trong thân yêu cầu là một quyết định duyệt, theo từng menu.
 *
 * Chỉ liệt kê những bước làm chứng từ CÓ HIỆU LỰC (khoá giá, tạo bút toán,
 * áp số kiểm kê). Gửi đi, trả lại, huỷ, đóng là việc của người lập → "sửa".
 */
export const DECISION_RULES: Readonly<Record<string, { field: "status" | "action"; approve: readonly string[] }>> = {
  purchaseOrders: { field: "status", approve: ["Confirmed"] },
  salesOrders: { field: "action", approve: ["confirm"] },
  stockMoves: { field: "action", approve: ["post"] },
  // Cấn trừ / bỏ cấn trừ phiếu ghi nợ đổi số tiền trả nhà cung cấp, cùng mức với ghi sổ phiếu chi.
  /*
   * 20/09/2026: thêm `activate` / `retire`. Bản đồ ghi sổ và chính sách giá thành
   * đi qua `/:id/actions` nên `decisionVerb` đọc thân yêu cầu chứ không nhìn
   * đoạn đường dẫn — không khai ở đây thì kích hoạt một bản đồ ghi sổ rơi vào
   * "sửa", đúng cái bẫy đã gặp ở định mức vật tư và quy trình công đoạn.
   */
  finance: { field: "action", approve: ["post", "reverse", "apply", "unapply", "activate", "retire"] },
  // Duyệt số tiền đòi nhà cung cấp là quyết định tiền; gửi, ghi phản hồi, đóng, huỷ là việc của người lập.
  supplierClaims: { field: "action", approve: ["approve"] },
  // Yêu cầu mua (PR-R02, 19/09/2026): duyệt và trả lại là quyết định của người duyệt. Gửi duyệt, rút lại, huỷ khi chưa duyệt, đóng là
  // việc của người lập / mua hàng (ô Sửa). Huỷ yêu cầu ĐÃ duyệt cần thêm ô Duyệt — dịch vụ kiểm vì phải biết trạng thái hiện tại.
  requisitions: { field: "action", approve: ["approve", "return"] },
  /*
   * Sản xuất (20/09/2026). Ranh giới giống Biến động kho: LÀM RA HÀNG là việc
   * của xưởng, ĐỔI TÌNH TRẠNG LỆNH hay ĐẢO MỘT PHIẾU ĐÃ GHI là quyết định.
   * - `release` cho xưởng bắt đầu và giữ công suất; `cancel` bỏ lệnh; `close`
   *   chốt lệnh kể cả khi làm thiếu → ô Duyệt.
   * - `void` đảo một phiếu cấp vật tư / báo công đã ghi → ô Duyệt.
   * - `post` (cấp vật tư, báo công, nhập thành phẩm) và `activate` /
   *   `deactivate` máy-trạm là việc hằng ngày → ô Sửa.
   * - `archive` (lệnh và máy-trạm) là ô Xoá; dịch vụ kiểm vì `decisionVerb`
   *   chỉ phân biệt được sửa với duyệt.
   */
  production: { field: "action", approve: ["release", "cancel", "close", "void"] },
  /*
   * Chất lượng (20/09/2026). Một menu phụ trách cả phiếu lỗi (NCR) lẫn tiêu
   * chuẩn kiểm, hai bộ hành động không trùng chữ nào nên dùng chung được:
   * - NCR `close` chốt phiếu lỗi (qua cổng 8D, và là chỗ duy nhất cho hàng
   *   trượt đi tiếp bằng đường "cho qua") và `cancel` bỏ phiếu → ô Duyệt.
   *   `open` là người kiểm dựng phiếu từ bằng chứng → ô Sửa.
   * - Tiêu chuẩn `activate` cho một bản có hiệu lực toàn nhà máy và `retire`
   *   ngừng bản đang dùng → ô Duyệt, y như định mức và quy trình công đoạn.
   *   `archive` là ô Xoá; dịch vụ kiểm vì `decisionVerb` chỉ phân biệt được
   *   sửa với duyệt.
   */
  quality: { field: "action", approve: ["close", "cancel", "activate", "retire"] },
};

/** Hành động thật của một yêu cầu quyết định, đọc từ thân đã parse. */
export function decisionVerb(menuKey: string, body: unknown): AccessVerb {
  const rule = DECISION_RULES[menuKey];
  if (!rule || !body || typeof body !== "object") return "update";
  const value = (body as Record<string, unknown>)[rule.field];
  return typeof value === "string" && rule.approve.includes(value) ? "approve" : "update";
}

export function verbForRequest(method: string, path: string): AccessVerb {
  const m = method.toUpperCase();
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return "view";
  if (m === "DELETE") return "delete";
  if (m === "PATCH" || m === "PUT") return "update";
  const last = (path.split("?")[0] ?? "").split("/").filter(Boolean).pop() ?? "";
  if (READ_SEGMENTS.has(last)) return "view";
  if (APPROVE_SEGMENTS.has(last)) return "approve";
  return ACTION_SEGMENTS.has(last) ? "update" : "create";
}

/**
 * Người này có được đi qua yêu cầu này không — dùng ở `requireAuth`.
 *
 * Với đường quyết định (`isDecisionPath`) chỉ cần MỘT trong hai quyền sửa/duyệt;
 * route phía sau sẽ đọc thân và chặn đúng hành động. Đặt ở đây để máy chủ và
 * bài kiểm dùng cùng một quy tắc.
 */
export function canRequest(access: UserAccess, menuKey: string, method: string, path: string): boolean {
  const verb = verbForRequest(method, path);
  if (canAccess(access, menuKey, verb) || canLookup(access, method, path)) return true;
  return verb === "update" && isDecisionPath(path) && canAccess(access, menuKey, "approve");
}

/* ─────────────────────────────  Quyền của một người  ──────────────────── */

export type AccessMode = "role" | "custom";
export type CustomerScope = "all" | "assigned";

/**
 * Khu vực dữ liệu của một tài khoản (Louis, 29/09/2026): đội Trung Quốc chỉ thấy phần Trung Quốc, đội Việt Nam chỉ thấy phần
 * Việt Nam, hai đội chỉ dùng chung sản phẩm, quản trị viên thấy hết.
 *
 * Giá trị trùng phân loại khách (`CustomerClass` — xưởng của khách đặt ở đâu, khách chốt 15/09/2026) vì đó là thứ khách đã khai
 * sẵn trong hệ thống: khách và mọi chứng từ gắn với khách thuộc khu vực của khách đó. "all" là cả hai khu vực.
 *
 * Tách khỏi `customerScope`: khu vực là ĐỘI của người đó, danh sách khách là việc được giao TRONG đội. Hai lớp chồng lên nhau
 * (nhân viên theo đơn đội Trung Quốc chỉ thấy khách được giao VÀ thuộc Trung Quốc), và khu vực áp cho cả tài khoản chạy theo vai
 * trò — không phải tick lại từng màn hình mới giới hạn được một người.
 */
export const DATA_REGIONS = ["all", "vietnam", "china"] as const;
export type DataRegion = (typeof DATA_REGIONS)[number];

export interface UserAccess {
  role: Role;
  mode: AccessMode;
  /** Chỉ dùng khi mode = "custom". */
  grants: Readonly<Record<string, readonly AccessVerb[]>>;
  /**
   * "assigned" = chỉ khách mình theo đơn (Roary 29/09/2026) — áp cho CẢ HAI chế độ, như khu vực: nhân viên theo đơn chạy theo
   * vai trò cũng giới hạn được mà không phải tick lại từng màn hình. Kế toán, kho, quản lý để "all" (Roary: "All customers").
   */
  customerScope: CustomerScope;
  /**
   * Khách người này theo đơn — chính là danh sách người theo đơn trên hồ sơ từng khách (`user_customers`, một nguồn). Chỉ giới
   * hạn tầm nhìn khi customerScope = "assigned"; nạp khi đó thôi.
   */
  customerIds: readonly string[];
  /** Được xem giá vốn / lợi nhuận; chỉ dùng khi mode = "custom". */
  canViewCost: boolean;
  /** Áp cho CẢ HAI chế độ; quản trị viên bỏ qua (xem `customerRegion`). */
  region: DataRegion;
  /**
   * Công ty ĐANG XEM trong phiên (yêu cầu v1.8 mục 1.1, 11/10/2026) — công tắc Tất cả / Việt Nam / Trung Quốc ở thanh trên, gửi theo
   * từng yêu cầu (header `X-Data-Region`), KHÔNG lưu vào hồ sơ người dùng. Chỉ thu hẹp: chỉ tài khoản được thấy cả hai công ty mới
   * dùng được (`canSwitchRegion`), tài khoản một khu vực bỏ qua.
   */
  viewRegion?: CustomerClass | null;
}

/** Quyền của một tài khoản chạy theo vai trò cũ — dùng khi chưa ai tuỳ chỉnh gì. */
export function roleAccess(role: Role, region: DataRegion = "all"): UserAccess {
  return { role, mode: "role", grants: {}, customerScope: "all", customerIds: [], canViewCost: false, region };
}

/**
 * Bộ mẫu: vai trò này tương đương những ô tick nào.
 *
 * Dùng để (a) chạy chế độ "theo vai trò" qua ĐÚNG một bộ giải quyền, và
 * (b) đổ sẵn bảng tick khi admin bấm "Dùng mẫu Quản lý" — người quản trị nhìn
 * thấy mình đang cho những gì thay vì phải tin một cái tên vai trò.
 */
export function presetGrants(role: Role): Record<string, AccessVerb[]> {
  const out: Record<string, AccessVerb[]> = {};
  for (const menu of MENUS) {
    const canView = menu.permission
      ? hasPermission(role, menu.permission)
      : menu.resource
        ? canReadResource(role, menu.resource)
        : hasPermission(role, "records:read");
    if (!canView) continue;
    const verbs: AccessVerb[] = ["view"];
    if (hasPermission(role, "records:create")) verbs.push("create");
    if (hasPermission(role, "records:update")) verbs.push("update");
    if (hasPermission(role, "records:delete")) verbs.push("delete");
    // Trước khi có ô "duyệt", duyệt đi chung đường với sửa. Giữ đúng như vậy
    // cho vai trò cũ để không tài khoản nào mất quyền vào ngày triển khai.
    if (hasPermission(role, "records:update")) verbs.push("approve");
    out[menu.key] = verbs;
  }
  return out;
}

/** Bảng tick thực sự đang có hiệu lực với người này. */
export function effectiveGrants(access: UserAccess): Record<string, readonly AccessVerb[]> {
  if (access.role === "admin") return presetGrants("admin");
  return access.mode === "custom" ? { ...access.grants } : presetGrants(access.role);
}

/**
 * Người này có được làm hành động đó ở menu đó không.
 *
 * Admin luôn đi qua: khách chốt "admin quản user thì bypass", và một hệ thống
 * mà admin có thể tự khoá mình ra ngoài bảng phân quyền là một hệ thống hỏng.
 */
export function canAccess(access: UserAccess, menuKey: string, verb: AccessVerb): boolean {
  if (access.role === "admin") return true;
  const verbs = effectiveGrants(access)[menuKey];
  return !!verbs && verbs.includes(verb);
}

/** Quyền cũ (`Permission`) suy ra từ bảng tick — để `requirePermission` dùng chung. */
export function effectivePermissions(access: UserAccess): Set<Permission> {
  if (access.role === "admin" || access.mode === "role") {
    return new Set(ROLE_PERMISSIONS[access.role]);
  }
  const grants = access.grants;
  const has = (menu: string, verb: AccessVerb) => !!grants[menu]?.includes(verb);
  const anyVerb = (verb: AccessVerb) => Object.values(grants).some((v) => v.includes(verb));

  const out = new Set<Permission>();
  if (anyVerb("view")) out.add("records:read");
  if (anyVerb("create")) out.add("records:create");
  // Route duyệt vẫn gác `records:update` từ trước; người chỉ có ô "duyệt" phải
  // qua được cái gác đó, còn hành động cụ thể đã bị `requireAuth` +
  // `requireDecision` chặn đúng theo ô tick.
  if (anyVerb("update") || anyVerb("approve")) out.add("records:update");
  if (anyVerb("delete")) out.add("records:delete");
  if (has("dashboard", "view")) out.add("dashboard:view");
  if (has("analytics", "view")) out.add("analytics:view");
  if (has("users", "view")) out.add("users:manage");
  if (has("settings", "view")) out.add("settings:manage");
  if (has("deletedRecords", "view")) out.add("deleted:manage");
  // Giá vốn là ô tick RIÊNG, không suy từ menu nào: nhiều menu đang mượn tạm
  // `supplier_cost:view` chỉ để nói "đây là nhân viên nội bộ", chẳng liên quan
  // gì tới giá. Suy từ chúng thì người được vào Kho bỗng nhiên đọc được giá vốn.
  if (access.canViewCost) out.add("supplier_cost:view");
  return out;
}

/**
 * Người này có được xem giá vốn / lợi nhuận không.
 *
 * Dùng cái này ở service, KHÔNG dùng `hasPermission(actor.role, …)`: vai trò
 * gốc không biết gì về ô tick "xem giá vốn" của tài khoản tuỳ chỉnh. Lỗi thật
 * tìm thấy 16/09/2026: viewer được tick giá vốn vẫn bị che ở màn Sản phẩm, còn
 * manager bị bỏ tick vẫn thấy.
 */
export function canSeeCost(access: UserAccess): boolean {
  return effectivePermissions(access).has("supplier_cost:view");
}

/**
 * Người này có được xem MÃ HÀNG CỦA KHÁCH (`products.customer_ref`, ví dụ "YKK 398") không — YC01-L1, 07/10/2026.
 *
 * Mã đó là dữ liệu bán hàng gắn với một khách (SAP để nó trong bản ghi vật tư theo khách của phân hệ bán hàng, không trên dữ liệu
 * vật tư dùng chung), và có mã để lộ tên khách hay thương hiệu. Ai được Xem ít nhất một màn mang thông tin khách (menu đánh dấu
 * `customerScoped`: khách hàng, báo giá, PI, hoá đơn, đơn mẫu, phiếu giao…) thì thấy; kho, mua hàng, sản xuất chỉ làm việc với mã
 * Hisense và không thấy. Tự chốt theo chuẩn — khách bác lại được.
 */
export function canSeeCustomerRef(access: UserAccess): boolean {
  return MENUS.some((m) => m.customerScoped && canAccess(access, m.key, "view"));
}

/**
 * Người này chỉ được thấy chứng từ của những khách nào.
 *
 * `null` = không giới hạn. Mảng rỗng = KHÔNG thấy chứng từ nào có khách —
 * đúng mặc định khách chốt trong họp: tài khoản chưa được gán khách thì không
 * xem được chứng từ của khách nào cả, chứ không phải xem được hết. Roary chốt
 * lại 29/09/2026: khách chưa có người theo đơn thì chỉ quản trị viên (và tài
 * khoản xem tất cả khách — kế toán, kho) thấy.
 *
 * Áp cho cả tài khoản chạy theo vai trò từ 29/09/2026 (trước đó chỉ tài khoản
 * tuỳ chỉnh): mọi tài khoản đang chạy có `customer_scope = 'all'`, nên không ai
 * đổi quyền vào ngày phát hành.
 */
export function allowedCustomerIds(access: UserAccess): readonly string[] | null {
  if (access.role === "admin") return null;
  if (access.customerScope === "all") return null;
  return access.customerIds;
}

/**
 * Người này có thấy khách riêng tư và liên hệ riêng tư không (Roary 30/09/2026: "only those with the highest permissions can
 * view them"). Chỉ quản trị viên — mức quyền cao nhất của hệ, như khu vực dữ liệu ("trừ quyền quản trị cao nhất"). Tài khoản
 * tuỳ chỉnh tick đủ mọi ô (mẫu Tổng giám đốc) vẫn KHÔNG thấy: ô tick là quyền theo màn hình, còn đây là ranh giới dữ liệu do
 * khách chốt. Cũng chỉ quản trị viên bật / tắt được hai cờ này.
 */
export function canSeePrivate(access: UserAccess): boolean {
  return access.role === "admin";
}

/**
 * Được đánh dấu một PI là "Chỉ quản trị viên" lúc tạo (anh Toàn chốt 11/10/2026). PI chỉ quản trị viên chỉ quản trị viên thấy
 * (`hideAdminOnlyPi`) và cờ không gỡ được (trigger 0070) — người không phải quản trị viên tick vào là tự làm mất PI mình vừa lập, không
 * ai ngoài quản trị viên mở lại được. Cùng quy tắc với khách riêng tư (30/09/2026): chỉ quản trị viên thấy ô và gửi cờ.
 */
export function canMarkAdminOnly(access: UserAccess): boolean {
  return access.role === "admin";
}

/**
 * Người này chỉ được thấy khách (và chứng từ của khách) thuộc khu vực nào. `null` = cả hai.
 *
 * Quản trị viên luôn `null` — khách chốt "trừ quyền quản trị cao nhất". Máy chủ lọc bằng câu truy vấn con trên
 * `customers.classification`, không bằng danh sách id: đội Việt Nam có 63 khách, vượt trần 100 tham số một câu của D1.
 */
export function customerRegion(access: UserAccess): CustomerClass | null {
  // Đang xem một công ty (công tắc ở thanh trên, v1.8 mục 1.1): mọi bộ lọc theo khu vực thu về đúng công ty đó — cùng một cửa, nên
  // mọi màn đang lọc theo khu vực (khách, chứng từ, sao kê, phân tích, nhà cung cấp, mua hàng…) đổi theo mà không sửa từng màn.
  if (access.viewRegion && canSwitchRegion(access)) return access.viewRegion;
  if (access.role === "admin") return null;
  return access.region === "all" ? null : access.region;
}

/**
 * Khu vực CỦA CHÍNH TÀI KHOẢN, bỏ qua công tắc công ty — dùng cho mọi chỗ GHI: đóng dấu khu vực lên chứng từ, phân loại mặc định,
 * chặn tạo / chuyển khách hay nhà cung cấp sang đội kia. Công tắc chỉ thu hẹp phần đang XEM (`customerRegion`), không bao giờ đổi
 * cách bản ghi được ghi: quản trị viên đang xem Trung Quốc vẫn tạo được khách Việt Nam, yêu cầu mua họ lập vẫn để trống khu vực.
 */
export function ownRegion(access: UserAccess): CustomerClass | null {
  return customerRegion({ ...access, viewRegion: null });
}

/** Được chọn xem từng công ty: quản trị viên và tài khoản thấy cả hai khu vực. Tài khoản một khu vực vốn chỉ có một công ty. */
export function canSwitchRegion(access: UserAccess): boolean {
  return access.role === "admin" || access.region === "all";
}
